import React from "react";
import HttpServices from "../../utils/Http.services";
import { translate, checkTranslation } from "../../utils/useful";
import { Pie, Doughnut } from "react-chartjs-2";
import { siteTheme, siteConfig } from "../../variables/config";
import { fakeFormsData } from "../../variables/fakeData";
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actions from '../../stores/actionsList';

const colors = siteTheme.primaryColors

class ProcessStatusWidget extends React.Component {
    state = {
        isLoading: true,
        data: [],
        stageCounts: {},
        stageColors: {},
        totalCount: 0,
        pieData: {
            labels: [],
            datasets: [{
                data: [],
                backgroundColor: [],
            }],
        },
        showLegend: false,
    }

    componentDidMount() {
        this.fetchData()
    }

    fetchData = () => {
        const widget = this.props.widget || this.props.data
        let body = {
            filter: {
                ...(widget.postBody || {}),
                frame: 'forms',
                'values.lastFollowUpResult': 'قرارداد' // Only contracts
            },
            limit: 1000,
            getCount: false
        }
        body.frame = widget.page || 'forms'
        body.page = widget.page || 'forms'
        body.limit = 1000 // Get all records for counting
        body.getCount = false

        // Use fake data if testEnv is true or if useFakeData prop is set
        if (siteConfig.testEnv || this.props.useFakeData) {
            // Filter fake data based on widget postBody filters
            let filteredFakeData = [...fakeFormsData]

            // Apply processOwner filter if present
            if (widget.postBody?.processOwner) {
                // For now, just use all fake data (processOwner filtering would need user info)
                // In real scenario, this would filter by the logged-in user's type
            }

            this.processData(filteredFakeData)
            return
        }
        if(this.props.widget.self){
            // if(!body.filter.values){
            //     body.filter.values={} 
            // }
            body.filter[`values.${this.props.widget.self}`] = this.props.user.info._id
        }
        HttpServices.request(widget.fetchUrl, body, (fetchResult, fetchError) => {
            if (fetchError) {
                console.error('Error fetching data:', fetchError)
                // Use fake data as fallback only in testEnv
                if (siteConfig.testEnv) {
                    this.processData(fakeFormsData)
                }
                return
            }

            const records = fetchResult?.info || []
            this.processData(records)
        })
    }

    // Helper to consistently resolve current user type for stage visibility
    getCurrentUserType = (widget) => {
        return this.props.user?.info?.userType
            || this.props.userType
            || widget?.userType
            || widget?.processOwner
            || 'admin'
    }

    processData = (records) => {
        const widget = this.props.widget || this.props.data

        // Determine current user type for stage visibility filtering
        const currentUserType = this.getCurrentUserType(widget)

        // Filter stages based on visibleFor (if defined)
        const allStages = widget.processStages || {}
        const stages = Object.fromEntries(
            Object.entries(allStages).filter(([_, stage]) => {
                if (!stage.visibleFor || !Array.isArray(stage.visibleFor)) return true
                return stage.visibleFor.includes(currentUserType)
            })
        )

        const stageCounts = {}
        const stageColors = {}
        let totalCount = records.length

        // Initialize counts
        Object.keys(stages).forEach(stageKey => {
            stageCounts[stageKey] = 0
        })

        // Count records by stage
        records.forEach(record => {
            const stage = record.values?.currentStage || record.currentStage
            if (stage && stageCounts.hasOwnProperty(stage)) {
                stageCounts[stage]++
            }
        })

        // Modern, vibrant color palette with better contrast and distinct hues
        const colorPalette = [
            '#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
            '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
            '#F43F5E', '#EAB308', '#A855F7', '#D946EF', '#0EA5E9',
            '#22C55E', '#64748B', '#84CC16', '#3B82F6', '#146a5d',
            '#1a8c7a', '#20ae97', '#26d0b4', '#6366f1', '#8b5cf6',
            '#a855f7', '#c084fc', '#d8b4fe', '#3b82f6', '#60a5fa',
            '#93c5fd', '#bfdbfe', '#dbeafe', '#ec4899', '#f472b6',
            '#f9a8d4', '#fbcfe8', '#fce7f3', '#f59e0b', '#fbbf24'
        ]

        // Assign colors to all stages first to maintain consistency
        let colorIndex = 0
        Object.entries(stages).forEach(([key, stage]) => {
            stageColors[key] = stage.color || colorPalette[colorIndex % colorPalette.length]
            colorIndex++
        })

        // Prepare pie chart data - show all stages, even with 0 count (unless showOnlyHaveData is true)
        const showOnlyHaveData = widget?.showOnlyHaveData === true

        const labels = []
        const data = []
        const backgroundColor = []

        Object.entries(stages).forEach(([key, stage]) => {
            const count = stageCounts[key] || 0
            // If showOnlyHaveData is true, skip stages with 0 count
            if (showOnlyHaveData && count === 0) {
                return
            }
            // If showOnlyHaveData is false, show all stages (even with 0 count)
            labels.push(checkTranslation(stage.name))
            data.push(count)
            backgroundColor.push(stageColors[key])
        })

        this.setState({
            data: records,
            stageCounts,
            stageColors,
            totalCount,
            pieData: {
                labels,
                datasets: [{
                    data,
                    backgroundColor,
                }],
            },
            isLoading: false,
        })
    }

    render() {
        const { stageCounts, stageColors, totalCount, pieData, isLoading } = this.state
        const widget = this.props.widget || this.props.data

        // Determine current user type for stage visibility filtering
        const currentUserType = this.getCurrentUserType(widget)

        // Filter stages based on visibleFor (if defined)
        const allStages = widget?.processStages || {}
        const stages = Object.fromEntries(
            Object.entries(allStages).filter(([_, stage]) => {
                if (!stage.visibleFor || !Array.isArray(stage.visibleFor)) return true
                return stage.visibleFor.includes(currentUserType)
            })
        )

        const showOnlyHaveData = widget?.showOnlyHaveData === true

        // If there are no stages visible for this user, don't render the widget at all
        if (Object.keys(stages).length === 0) {
            return null
        }

        if (isLoading) {
            return (
                <div className="w-100 py-3 text-center">
                    <p>{translate('Loading...')}</p>
                </div>
            )
        }

        // Filter stages based on showOnlyHaveData flag (zero-count stages hidden when true)
        const stagesToShow = showOnlyHaveData
            ? Object.entries(stages).filter(([key]) => {
                const count = stageCounts[key] || 0
                return count > 0
            })
            : Object.entries(stages)

        // If there is no data at all or nothing to show, don't render the widget
        const hasVisibleStages = stagesToShow.length > 0 && totalCount > 0
        if (!hasVisibleStages) {
            return null
        }

        return (
            <div className="w-100 py-2">
                <div className="row m-0">
                    {/* Status boxes */}
                    <div className="col-12 mb-3">
                        <div className="d-flex" style={{ overflowX: 'auto', overflowY: 'hidden', width: '100%', gap: '12px', paddingBottom: '8px' }}>
                            {stagesToShow.map(([key, stage]) => {
                                const count = stageCounts[key] || 0
                                // Double check: if showOnlyHaveData is true, don't render if count is 0
                                if (showOnlyHaveData && !count) {
                                    return null
                                }
                                const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0
                                const color = stageColors[key] || '#ccc'
                                return (
                                    <div key={key} style={{ flexShrink: 0, minWidth: '200px', maxWidth: '200px' }}>
                                        <div className="p-3 h-100" style={{
                                            background: `linear-gradient(135deg, ${color}15, #ffffff)`,
                                            border: `1px solid ${color}40`,
                                            borderRadius: 12,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                            height: '100%'
                                        }}>
                                            <p className="mb-1" style={{ fontSize: 24, fontWeight: 'bold', color: color }}>
                                                {count}
                                            </p>
                                            <p className="mb-0" style={{ fontSize: 11, color: '#666' }}>
                                                {checkTranslation(stage.name)}
                                            </p>
                                            <p className="mb-0" style={{ fontSize: 10, color: '#999' }}>
                                                {percentage}%
                                            </p>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>

                    {/* Pie Chart */}
                    {pieData.labels.length > 0 && (
                        <div className="col-12 col-md-6">
                            <div className="p-3" style={{
                                background: '#fff',
                                borderRadius: 12,
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                overflow: 'visible'
                            }}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0" style={{ fontSize: 14, fontWeight: 600 }}>
                                        {translate('Distribution by Stage')}
                                    </h6>
                                    <button
                                        onClick={() => this.setState({ showLegend: !this.state.showLegend })}
                                        style={{
                                            background: this.state.showLegend
                                                ? `linear-gradient(135deg, ${colors.main1} 0%, ${colors.main1} 75%, ${colors.main2} 100%)`
                                                : `linear-gradient(135deg, ${colors.main3}50, ${colors.main3}60)`,
                                            border: `2px solid ${this.state.showLegend ? colors.main1 : colors.main3 + '80'}`,
                                            borderRadius: 20,
                                            padding: '6px 16px',
                                            fontSize: 11,
                                            fontWeight: 600,
                                            color: this.state.showLegend ? '#fff' : colors.main1,
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            boxShadow: this.state.showLegend ? `0 2px 8px ${colors.main1}30` : 'none'
                                        }}
                                    >
                                        {this.state.showLegend ? translate('Hide Legend') : translate('Show Legend')}
                                    </button>
                                </div>
                                <div style={{ height: 400, padding: '20px', overflow: 'hidden', position: 'relative', minHeight: 400 }}>
                                    <Doughnut
                                        data={pieData}
                                        options={{
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            cutout: '25%',
                                            layout: {
                                                padding: {
                                                    left: 0,
                                                    right: 0,
                                                    top: 0,
                                                    bottom: this.state.showLegend ? 50 : 0
                                                }
                                            },
                                            plugins: {
                                                legend: {
                                                    display: this.state.showLegend,
                                                    position: 'bottom',
                                                    align: 'center',
                                                    labels: {
                                                        boxWidth: 14,
                                                        padding: 10,
                                                        font: { size: 11, weight: '500' },
                                                        usePointStyle: true,
                                                        pointStyle: 'circle',
                                                        generateLabels: function (chart) {
                                                            const data = chart.data
                                                            if (data.labels.length && data.datasets.length) {
                                                                return data.labels.map((label, i) => {
                                                                    const value = data.datasets[0].data[i]
                                                                    const color = data.datasets[0].backgroundColor[i]
                                                                    const total = data.datasets[0].data.reduce((a, b) => a + b, 0)
                                                                    const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                                    return {
                                                                        text: `${label}: ${value} (${percentage}%)`,
                                                                        fillStyle: color,
                                                                        strokeStyle: color,
                                                                        lineWidth: 0,
                                                                        hidden: false,
                                                                        index: i
                                                                    }
                                                                })
                                                            }
                                                            return []
                                                        }
                                                    }
                                                },
                                                datalabels: {
                                                    display: false
                                                },
                                                tooltip: {
                                                    callbacks: {
                                                        label: function (context) {
                                                            const label = context.label || ''
                                                            const value = context.parsed || context.raw
                                                            const total = context.dataset.data.reduce((a, b) => a + b, 0)
                                                            const percentage = total > 0 ? Math.round((value / total) * 100) : 0
                                                            return `${label}: ${value} (${percentage}%)`
                                                        }
                                                    }
                                                }
                                            },
                                            elements: {
                                                arc: {
                                                    borderWidth: 2,
                                                    borderColor: '#fff'
                                                }
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Summary */}
                    <div className="col-12 col-md-6">
                        <div className="p-3" style={{
                            background: `linear-gradient(135deg, ${colors.main1}10, #ffffff)`,
                            borderRadius: 12,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                            border: `1px solid ${colors.main1}30`
                        }}>
                            <h6 className="mb-3" style={{ fontSize: 14, fontWeight: 600, color: colors.main1 }}>
                                {translate('Summary')}
                            </h6>
                            <div className="mb-2">
                                <p className="mb-1" style={{ fontSize: 32, fontWeight: 'bold', color: colors.main1 }}>
                                    {totalCount}
                                </p>
                                <p className="mb-0" style={{ fontSize: 12, color: '#666' }}>
                                    {translate('Total Records')}
                                </p>
                            </div>
                            <div className="mt-3">
                                <p className="mb-1" style={{ fontSize: 12, color: '#666' }}>
                                    {translate('Active Stages')}: {Object.values(stageCounts).filter(c => c > 0).length}
                                </p>
                                <p className="mb-0" style={{ fontSize: 12, color: '#666' }}>
                                    {translate('Completed')}: {stageCounts.pedigree_letters || 0}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

const mapStateToProps = state => ({ settings: state.settings, user: state.user })
const mapDispatchToProps = dispatch => ({ actions: bindActionCreators(actions, dispatch) })

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProcessStatusWidget);
